/**
 * Box 10: imported whole-day outcomes for the period against the Supabase
 * spend of the same days (ruling 5, migration 113). A null figure is "—":
 * not imported (or not synced) is not zero,
 * and a ratio over a 0 or null denominator is not measurable.
 *
 * Currencies are never added together (rules-04 B1, migration 125). In one
 * currency, revenue, spend and Cost per Lead carry its marker. Revenue is
 * imported with no currency; Media ROAS divides it by spend, so it is read in
 * spend's currency (an assumption, not a fact of the upload). With spend in
 * more than one currency, or on an account of unknown currency, spend is one
 * line per currency, revenue a bare number, and Cost per Lead and Media ROAS
 * "—" with the reason. A currency nobody knows never gets a ฿.
 *
 * @module components/looker/app/OutcomesBox
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatConversions } from '@/utils/format';
import { formatCurrency } from '@/config/brand';
import type { OutcomePeriod } from '@/services/outcomeImport/outcomeImportService';
import type { OutcomeMoney } from '@/services/looker/lookerAppData';
import { BoxMessage, DASH } from './appBoxShared';

interface Props {
  outcomes: OutcomePeriod;
  costPerLead: number | null;
  mediaRoas: number | null;
  money: OutcomeMoney;
}

const count = (n: number | null): string => (n === null ? DASH : formatConversions(n));
/** In `currency`; with none known, no marker. */
const amount = (n: number | null, currency: string | null): string => {
  if (n === null) return DASH;
  return currency === null ? formatCurrency(n, false) : formatCurrency(n, true, currency);
};
const ratio = (n: number | null): string => (n === null ? DASH : `${n.toFixed(2)}×`);

export const OutcomesBox: React.FC<Props> = ({ outcomes: o, costPerLead, mediaRoas, money }) => {
  const { t } = useTranslation();
  if (o.days === 0) return <BoxMessage text={t('looker.app.outcomes.noneInPeriod')} />;
  const currency = money.kind === 'one' ? money.currency : null;
  const spend: React.ReactNode = money.kind === 'one' ? amount(o.spend, currency) : money.spends.map((e) => (
    <span key={e.currency ?? ''} className="block">
      {e.currency === null
        ? `${t('looker.app.outcomes.unknownCurrencyLabel')} ${formatCurrency(e.spend, false)}`
        : formatCurrency(e.spend, true, e.currency)}
    </span>
  ));
  const figures: Array<[string, React.ReactNode]> = [
    [t('looker.app.table.leads'), count(o.leads)],
    [t('looker.app.table.bookings'), count(o.bookings)],
    [t('looker.app.table.sales'), count(o.sales)],
    [t('looker.app.table.revenue'), amount(o.revenue, currency)],
    [t('looker.app.table.spend'), spend],
    [t('looker.app.table.costPerLead'), money.kind === 'one' ? amount(costPerLead, currency) : DASH],
    [t('looker.app.table.mediaRoas'), money.kind === 'one' ? ratio(mediaRoas) : DASH],
  ];
  return (
    <div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        {figures.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-neutral-700">{label}</dt>
            <dd className="text-lg font-semibold tabular-nums text-neutral-900 break-words">{value}</dd>
          </div>
        ))}
      </dl>
      {money.kind === 'split' && (
        <p className="mt-3 text-xs text-neutral-700">
          {money.reason === 'mixed' ? t('looker.app.outcomes.mixedCurrency') : t('looker.app.outcomes.unknownCurrency')}
        </p>
      )}
      <p className="mt-3 text-xs text-neutral-700">{t('looker.app.outcomes.days', { days: o.days })}</p>
    </div>
  );
};
